
/**
 * Energy calculation cache and optimization utilities
 */

import { EnergyDataPoint, EnergyBlockType } from "./types";
import { createPerformanceLogger } from "@/lib/utils/performance-logger";

const logger = createPerformanceLogger('EnergyCache');

interface EnergyLookupTable {
  [hour: number]: number;
}

interface EnergyThresholds {
  power: number;
  focus: number;
  drift: number;
}

export const ENERGY_THRESHOLDS: EnergyThresholds = {
  power: 80, // 80% energy for power zones
  focus: 60, // 60% energy for focus zones  
  drift: 30  // 30% energy for drift zones
};

// Cache for energy lookup tables
const energyLookupCache = new Map<string, EnergyLookupTable>();

// Cache for energy calculations
const energyCalcCache = new Map<string, number>();

/**
 * Create a pre-computed energy lookup table from forecast data
 */
export function createEnergyLookupTable(energyForecast: EnergyDataPoint[]): EnergyLookupTable {
  const lookup: EnergyLookupTable = {};
  
  logger.debug(`Creating energy lookup table from ${energyForecast.length} data points`);
  
  for (const dataPoint of energyForecast) {
    // Handle different data point structures
    let hour: number;
    let energy: number;
    
    // Extract hour
    if (dataPoint.hour !== undefined) {
      hour = dataPoint.hour;
    } else if (dataPoint.time) {
      // Parse hour from time string like "10:00" or just "10"
      const timeStr = dataPoint.time.toString();
      hour = parseInt(timeStr.split(':')[0]);
    } else {
      logger.warn('DataPoint missing hour and time:', dataPoint);
      continue;
    }
    
    // Extract energy value - try multiple fields
    if (dataPoint.energy !== undefined) {
      energy = dataPoint.energy;
    } else if (dataPoint.value !== undefined) {
      energy = dataPoint.value * 100; // Convert 0-1 range to percentage
    } else if (dataPoint.level !== undefined) {
      energy = dataPoint.level >= 1 ? dataPoint.level : dataPoint.level * 100; // Handle both formats
    } else {
      logger.warn('DataPoint missing energy value:', dataPoint);
      continue;
    }
    
    // Ensure energy is in percentage format (0-100)
    if (energy <= 1) {
      energy = energy * 100;
    }
    
    lookup[hour] = energy;
    logger.verbose(`Lookup[${hour}] = ${energy.toFixed(1)}%`);
  }
  
  // Fill in any missing hours with interpolated values
  for (let hour = 0; hour < 24; hour++) {
    if (lookup[hour] === undefined) {
      const prevHour = (hour - 1 + 24) % 24;
      const nextHour = (hour + 1) % 24;
      
      if (lookup[prevHour] !== undefined && lookup[nextHour] !== undefined) {
        lookup[hour] = (lookup[prevHour] + lookup[nextHour]) / 2;
        logger.verbose(`Interpolated hour ${hour}: ${lookup[hour].toFixed(1)}%`);
      } else {
        // Default fallback energy
        lookup[hour] = 50;
        logger.verbose(`Fallback hour ${hour}: 50%`);
      }
    }
  }
  
  logger.debug(`Energy lookup table created with ${Object.keys(lookup).length} hours`);
  return lookup;
}

/**
 * Get cached energy lookup table or create new one
 */
export function getCachedEnergyLookup(energyForecast: EnergyDataPoint[], cacheKey: string): EnergyLookupTable {
  if (energyLookupCache.has(cacheKey)) {
    logger.debug(`Using cached energy lookup table: ${cacheKey}`);
    return energyLookupCache.get(cacheKey)!;
  }
  
  logger.debug(`Creating new energy lookup table: ${cacheKey}`);
  const lookup = createEnergyLookupTable(energyForecast);
  energyLookupCache.set(cacheKey, lookup);
  
  // Clean old cache entries if cache gets too large
  if (energyLookupCache.size > 50) {
    const firstKey = energyLookupCache.keys().next().value;
    energyLookupCache.delete(firstKey);
    logger.debug(`Cleaned old cache entry: ${firstKey}`);
  }
  
  return lookup;
}

/**
 * Fast energy level lookup with O(1) performance and enhanced logging
 */
export function getEnergyLevelFast(hour: number, energyLookup: EnergyLookupTable): number {
  // Handle fractional hours (e.g., 10.5 for 10:30)
  const baseHour = Math.floor(hour);
  const fraction = hour - baseHour;
  
  logger.verbose(`Looking up energy for hour ${hour} (base: ${baseHour}, fraction: ${fraction})`);
  
  // Direct lookup for whole hours
  if (fraction === 0 && energyLookup[baseHour] !== undefined) {
    const energy = energyLookup[baseHour];
    logger.verbose(`Direct lookup hour ${baseHour}: ${energy}%`);
    return energy;
  }
  
  // Interpolation for fractional hours
  const currentHour = energyLookup[baseHour];
  const nextHour = energyLookup[(baseHour + 1) % 24];
  
  if (currentHour !== undefined && nextHour !== undefined) {
    const interpolated = currentHour + (nextHour - currentHour) * fraction;
    logger.verbose(`Interpolated hour ${hour}: ${interpolated.toFixed(1)}% (from ${currentHour}% to ${nextHour}%)`);
    return interpolated;
  }
  
  // Single hour fallback
  if (currentHour !== undefined) {
    logger.verbose(`Single hour fallback ${baseHour}: ${currentHour}%`);
    return currentHour;
  }
  
  // Ultimate fallback
  logger.warn(`No energy data found for hour ${hour}, using 50% fallback`);
  return 50; // 50% default energy
}

/**
 * Check if energy level meets preference requirement with proper thresholds
 */
export function meetsEnergyRequirement(energyPercentage: number, preference: EnergyBlockType): boolean {
  logger.verbose(`Checking energy requirement: ${energyPercentage.toFixed(1)}% vs ${preference} preference`);
  
  let meets = false;
  
  switch (preference) {
    case 'power':
      meets = energyPercentage >= ENERGY_THRESHOLDS.power;
      break;
    case 'focus':
      meets = energyPercentage >= ENERGY_THRESHOLDS.focus && energyPercentage < ENERGY_THRESHOLDS.power;
      break;
    case 'drift':
      meets = energyPercentage >= ENERGY_THRESHOLDS.drift && energyPercentage < ENERGY_THRESHOLDS.focus;
      break;
    case 'any':
    case 'ignore':
      meets = true;
      break;
    default:
      meets = true;
  }
  
  logger.verbose(`Energy ${energyPercentage.toFixed(1)}% ${meets ? 'MEETS' : 'DOES NOT MEET'} ${preference} requirement (threshold: ${ENERGY_THRESHOLDS[preference as keyof EnergyThresholds] || 'none'}%)`);
  return meets;
}

/**
 * Batch process energy levels for multiple time slots with enhanced debugging
 */
export function batchProcessEnergyLevels(
  timeSlots: Date[],
  energyLookup: EnergyLookupTable,
  preference: EnergyBlockType
): { time: Date; energyLevel: number; meetsRequirement: boolean }[] {
  logger.debug(`Batch processing ${timeSlots.length} time slots for ${preference} preference`);
  
  return timeSlots.map((time, index) => {
    const hour = time.getHours() + time.getMinutes() / 60;
    const energyLevel = getEnergyLevelFast(hour, energyLookup);
    const meetsRequirement = meetsEnergyRequirement(energyLevel, preference);
    
    if (index < 5) { // Log first few for debugging
      logger.debug(`Slot ${index}: ${time.getHours()}:${time.getMinutes().toString().padStart(2, '0')} = ${energyLevel.toFixed(1)}% (${meetsRequirement ? 'PREFERRED' : 'AVAILABLE'})`);
    }
    
    return {
      time,
      energyLevel,
      meetsRequirement
    };
  });
}

/**
 * Get cached energy calculation result
 */
export function getCachedEnergyCalc(cacheKey: string): number | undefined {
  return energyCalcCache.get(cacheKey);
}

/**
 * Set cached energy calculation result
 */
export function setCachedEnergyCalc(cacheKey: string, result: number): void {
  energyCalcCache.set(cacheKey, result);
  
  // Clean old cache entries if cache gets too large
  if (energyCalcCache.size > 200) {
    const firstKey = energyCalcCache.keys().next().value;
    energyCalcCache.delete(firstKey);
  }
}

/**
 * Clear all energy caches
 */
export function clearEnergyCaches(): void {
  energyLookupCache.clear();
  energyCalcCache.clear();
}

/**
 * Get cache statistics for debugging
 */
export function getEnergyCacheStats() {
  return {
    lookupCacheSize: energyLookupCache.size,
    calcCacheSize: energyCalcCache.size
  };
}
